import React, { useState } from 'react';
import { useParams } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getQueryFn, apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface StoreFormat {
  id: number;
  name: string;
  description?: string | null;
  retailerId: number;
}

export default function RetailerStoreFormatsPage({ retailerIdOverride }: { retailerIdOverride?: number } = {}) {
  const params = useParams() as { retailerId?: string };
  const retailerIdNum = retailerIdOverride ?? (params?.retailerId ? Number(params.retailerId) : NaN);
  const retailerId = String(retailerIdNum);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const listQuery = useQuery<StoreFormat[] | null>({
    queryKey: [`/api/store-formats?retailerId=${retailerId}`],
    queryFn: getQueryFn({ on401: 'returnNull' }),
    enabled: Number.isFinite(retailerIdNum),
  });

  const createMutation = useMutation({
    mutationFn: async (payload: { name: string; description?: string }) => {
      const body: any = { name: payload.name, retailerId: retailerIdNum };
      if (payload.description?.trim()) body.description = payload.description.trim();
      await apiRequest('/api/store-formats', 'POST', body);
    },
    onSuccess: () => {
      setName('');
      setDescription('');
      queryClient.invalidateQueries({ queryKey: [`/api/store-formats?retailerId=${retailerId}`] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest(`/api/store-formats/${id}`, 'DELETE');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/store-formats?retailerId=${retailerId}`] });
      toast({
        title: "Success",
        description: "Store format deleted successfully",
      });
    },
    onError: (error: any) => {
      // Extract detailed message from backend response
      const errorData = error.data || {};
      const message = errorData.message || error.message || "Failed to delete store format";
      const storeCount = errorData.storeCount;

      // Build comprehensive error description
      let description = message;
      if (storeCount && !message.includes('store(s)')) {
        description = `Cannot delete this store format because it is assigned to ${storeCount} store(s). Please reassign or remove the stores first.`;
      }

      toast({
        title: "Cannot Delete Store Format",
        description,
        variant: "destructive",
        duration: 8000, // Show for longer since it has actionable guidance
      });
    },
  });

  const formats = listQuery.data || [];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    createMutation.mutate({ name: name.trim(), description });
  };

  return (
    <div className="p-4">
      <h1 className="text-xl font-semibold mb-4">Store Formats</h1>

      <form onSubmit={handleSubmit} className="mb-4 flex flex-wrap gap-2 items-center">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Format name (required)"
          className="border rounded px-3 py-2 w-64"
          required
        />
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          className="border rounded px-3 py-2 w-72"
        />
        <Button
          type="submit"
          disabled={createMutation.isPending}
        >
          <Plus className="h-4 w-4 mr-2" />
          {createMutation.isPending ? 'Adding…' : 'Add Format'}
        </Button>
      </form>

      {listQuery.isLoading ? (
        <div>Loading…</div>
      ) : listQuery.isError ? (
        <div className="text-red-600">Error loading store formats</div>
      ) : !formats || formats.length === 0 ? (
        <div className="text-gray-600">No store formats defined for this retailer yet.</div>
      ) : (
        <div className="overflow-auto">
          <table className="min-w-[500px] w-full border border-gray-200 bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 border-b">Name</th>
                <th className="text-left px-3 py-2 border-b">Description</th>
                <th className="text-left px-3 py-2 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {formats.map((format) => (
                <tr key={format.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 border-b">{format.name}</td>
                  <td className="px-3 py-2 border-b text-gray-700">{format.description || '-'}</td>
                  <td className="px-3 py-2 border-b">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteMutation.mutate(format.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


